import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { scopedPath } from '../../utils/shopSlug';
import {
  LayoutDashboard,
  Package,
  Boxes,
  ShoppingCart,
  Truck,
  Undo2,
  ShieldAlert,
  Tag,
  Star,
  HelpCircle,
  Wallet,
  CreditCard,
  FileText,
  BarChart3,
  UserCircle,
  MessageCircle,
  History,
  Bell,
  Store,
  Sparkles,
  TrendingUp,
  Users,
  Clock,
  CalendarClock,
  ChefHat,
  X,
  ChevronDown,
} from 'lucide-react';
import clsx from 'clsx';
import { useAuth } from '../../contexts/AuthContext';
import { useShopInfo } from '../../hooks/useShopInfo';
import { useMessagesUnread } from '../../hooks/useMessagesUnread';
import { usePortalNavigation } from '../../hooks/usePortalNavigation';
import { portalPageEnabled, portalRoutePath } from '../../utils/portalNavigation';

type NavLeaf = {
  to: string;
  icon: typeof LayoutDashboard;
  label: string;
  /** Short plain-language tooltip shown on hover. */
  desc?: string;
  ai?: boolean;
  /** Tier marker for merchant plan features. */
  tier?: 'Pro' | 'Growth';
  /** Food-vertical page — shown only when the shop enables food. */
  food?: boolean;
  grocery?: boolean;
};

interface NavSection {
  id: string;
  label: string;
  items: NavLeaf[];
}

export const navSections: NavSection[] = [
  {
    id: 'sell',
    label: 'Sell',
    items: [
      { to: '/products', icon: Package, label: 'Products' },
      { to: '/menu', icon: ChefHat, label: 'Menu', food: true },
      { to: '/inventory', icon: Boxes, label: 'Inventory' },
      {
        to: '/offers',
        icon: Store,
        label: 'Offers',
        desc: "Sell another seller's approved listing from your own stock location, with the store's approval.",
      },
      { to: '/grocery/unit-pricing', icon: Tag, label: 'Unit-pricing snapshots', grocery: true, desc: 'Preview and publish vendor-entered grocery product snapshots.' },
      { to: '/orders', icon: ShoppingCart, label: 'Orders' },
      { to: '/returns', icon: Undo2, label: 'Returns' },
      { to: '/disputes', icon: ShieldAlert, label: 'Disputes' },
    ],
  },
  {
    id: 'delivery',
    label: 'Delivery',
    items: [
      {
        to: '/delivery',
        icon: Truck,
        label: 'Delivery',
        desc: 'Manage deliveries and tracking. Courier dispatch is available on Pro and higher plans.',
      },
      {
        to: '/availability',
        icon: Clock,
        label: 'Availability',
        desc: "Set when you're open and how many orders you can handle — checkout stops taking orders while you're closed or maxed out.",
      },
      {
        to: '/slots',
        icon: CalendarClock,
        label: 'Delivery slots',
        tier: 'Growth',
        desc: 'Offer bookable delivery windows and in-store pickup (BOPIS) at checkout.',
      },
      {
        to: '/console',
        icon: ChefHat,
        label: 'Live order screen',
        tier: 'Growth',
        desc: 'A live order board for a counter tablet — new orders pop in with a sound; move each from New to Ready in one tap.',
      },
    ],
  },
  {
    id: 'money',
    label: 'Money',
    items: [
      { to: '/payouts', icon: Wallet, label: 'Payouts' },
      { to: '/payouts/method', icon: CreditCard, label: 'Payout method' },
      { to: '/statements', icon: FileText, label: 'Statements' },
      { to: '/invoices', icon: Wallet, label: 'Invoices' },
      { to: '/coupons', icon: Tag, label: 'Discount codes' },
    ],
  },
  {
    id: 'grow',
    label: 'Grow',
    items: [
      { to: '/analytics', icon: BarChart3, label: 'Analytics' },
      { to: '/ai/forecast', icon: TrendingUp, label: 'Sales forecast', ai: true },
      { to: '/ai/listing-quality', icon: Sparkles, label: 'Listing quality', ai: true },
      { to: '/storefront', icon: Store, label: 'Storefront' },
      { to: '/referrals', icon: Users, label: 'Refer a vendor' },
    ],
  },
  {
    id: 'customers',
    label: 'Customers',
    items: [
      { to: '/messages', icon: MessageCircle, label: 'Messages' },
      { to: '/reviews', icon: Star, label: 'Reviews' },
      { to: '/questions', icon: HelpCircle, label: 'Q&A' },
      { to: '/notifications', icon: Bell, label: 'Notifications' },
      { to: '/support', icon: Sparkles, label: 'AI assistant', ai: true },
      { to: '/support/cases', icon: HelpCircle, label: 'Support cases' },
      { to: '/support/shopper-cases', icon: HelpCircle, label: 'Shopper requests' },
    ],
  },
  {
    id: 'account',
    label: 'Account',
    items: [
      { to: '/profile', icon: UserCircle, label: 'Business profile' },
      { to: '/activity', icon: History, label: 'Admin activity' },
    ],
  },
];

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const navigation = usePortalNavigation();
  const { vendor, user } = useAuth();
  const { pathname } = useLocation();
  const { shopInfo } = useShopInfo();
  const brandLogo = shopInfo.logo_url || vendor?.logo_url || '';
  const brandName = shopInfo.portal_name || 'Vendor Portal';
  const brandInitial = (brandName.trim().charAt(0) || 'V').toUpperCase();
  const brandTint = shopInfo.primary_color || '#6366f1';
  const aiEnabled = vendor?.features?.vendor_ai === true;
  const foodEnabled = vendor?.features?.food === true;
  const groceryEnabled = vendor?.features?.grocery === true;
  const unreadMessages = useMessagesUnread(portalPageEnabled('/messages', navigation));
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const visibleSections = navSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => (!item.ai || aiEnabled) && (!item.food || foodEnabled)
        && (!item.grocery || groceryEnabled) && portalPageEnabled(item.to, navigation)),
    }))
    .filter((section) => section.items.length > 0);

  // `useLocation()` normally strips the router basename (`/vendors`) while
  // `window.location.pathname` does not. Compare only the protected route
  // suffix so active state remains correct in either representation and does
  // not depend on the browser-global slug resolver.
  const routePath = useMemo(() => {
    return portalRoutePath(pathname);
  }, [pathname]);

  const isItemActive = (to: string) =>
    routePath === to || routePath.startsWith(`${to}/`);

  const activeSectionId = visibleSections.find((section) =>
    section.items.some((item) => isItemActive(item.to)),
  )?.id;

  // A deliberately collapsed active section may stay closed on the current
  // page, but navigation must reveal the new active destination again.
  useEffect(() => {
    if (!activeSectionId) return;
    setExpanded((current) =>
      current[activeSectionId] === true
        ? current
        : { ...current, [activeSectionId]: true },
    );
  }, [activeSectionId, pathname]);

  return (
    <>
      {open && (
        <button
          type="button"
          className="fixed inset-0 z-40 cursor-default bg-black/50 lg:hidden"
          onClick={onClose}
          aria-label="Close navigation"
        />
      )}

      <aside
        aria-label="Vendor portal navigation"
        className={clsx(
          'fixed left-0 top-0 z-50 flex h-full w-64 flex-col bg-sidebar shadow-2xl transition-transform duration-200 lg:static lg:z-auto lg:translate-x-0 lg:shadow-none',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-white/10 px-4">
          <div className="flex min-w-0 items-center gap-3">
            {brandLogo ? (
              <div
                className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-xl ring-1 ring-white/15"
                style={{ backgroundColor: brandTint }}
              >
                <img
                  src={brandLogo}
                  alt={`${brandName} logo`}
                  className="h-full w-full object-contain"
                />
              </div>
            ) : (
              <div
                className="vendoriq-on-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-sm font-bold ring-1 ring-white/15"
                style={{ backgroundColor: brandTint }}
                aria-hidden="true"
              >
                {brandInitial}
              </div>
            )}
            <div className="min-w-0">
              <span className="block truncate text-sm font-semibold text-white" title={brandName}>
                {brandName}
              </span>
              <span className="block truncate text-[11px] text-gray-400">
                Vendor workspace
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation"
            className="rounded-lg p-1.5 text-gray-400 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-primary lg:hidden"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {portalPageEnabled('/dashboard', navigation) && <NavLink
            to={scopedPath('/dashboard')}
            onClick={onClose}
            className={({ isActive }) =>
              clsx(
                'mb-3 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-white/10 text-white shadow-sm'
                  : 'text-gray-300 hover:bg-white/5 hover:text-white',
              )
            }
          >
            <LayoutDashboard className="h-5 w-5 shrink-0" aria-hidden="true" />
            <span>Dashboard</span>
          </NavLink>}

          <div className="space-y-1">
            {visibleSections.map((section) => {
              const sectionActive = section.items.some((item) => isItemActive(item.to));
              const sectionOpen = expanded[section.id] ?? sectionActive;
              const sectionUnread =
                section.id === 'customers' ? unreadMessages : 0;

              return (
                <div key={section.id}>
                  <button
                    type="button"
                    onClick={() =>
                      setExpanded((current) => ({
                        ...current,
                        [section.id]: !sectionOpen,
                      }))
                    }
                    aria-expanded={sectionOpen}
                    aria-controls={`sidebar-section-${section.id}`}
                    className={clsx(
                      'flex w-full items-center rounded-lg px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider transition-colors',
                      sectionActive
                        ? 'text-white'
                        : 'text-gray-500 hover:bg-white/5 hover:text-gray-300',
                    )}
                  >
                    <span className="flex-1">{section.label}</span>
                    {sectionUnread > 0 && !sectionOpen && (
                      <span className="mr-2 inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] text-white">
                        {sectionUnread > 99 ? '99+' : sectionUnread}
                      </span>
                    )}
                    <ChevronDown
                      className={clsx(
                        'h-4 w-4 transition-transform',
                        sectionOpen && 'rotate-180',
                      )}
                      aria-hidden="true"
                    />
                  </button>

                  {sectionOpen && (
                    <div id={`sidebar-section-${section.id}`} className="mb-2 space-y-0.5">
                      {section.items.map((item) => {
                        const Icon = item.icon;
                        const badgeCount = item.to === '/messages' ? unreadMessages : 0;
                        const itemActive =
                          isItemActive(item.to) &&
                          !(
                            item.to === '/payouts' &&
                            routePath.startsWith('/payouts/method')
                          );
                        return (
                          <NavLink
                            key={item.to}
                            to={scopedPath(item.to)}
                            onClick={onClose}
                            title={item.desc}
                            className={() =>
                              clsx(
                                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                                itemActive
                                  ? 'bg-white/10 text-white'
                                  : 'text-gray-400 hover:bg-white/5 hover:text-white',
                              )
                            }
                          >
                            <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
                            <span className="flex-1">{item.label}</span>
                            {item.tier && (
                              <span
                                className="ml-1 inline-flex items-center rounded-md bg-amber-400/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-300 ring-1 ring-inset ring-amber-400/30"
                                title={`${item.tier} plan feature`}
                              >
                                {item.tier}
                              </span>
                            )}
                            {item.ai && (
                              <span
                                className="ml-1 inline-flex items-center gap-0.5 rounded-md bg-indigo-400/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-indigo-300 ring-1 ring-inset ring-indigo-400/30"
                                title="AI-powered"
                              >
                                <Sparkles className="h-2.5 w-2.5" aria-hidden="true" />
                                AI
                              </span>
                            )}
                            {badgeCount > 0 && (
                              <span
                                className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-semibold text-white"
                                aria-label={`${badgeCount} unread`}
                              >
                                {badgeCount > 99 ? '99+' : badgeCount}
                              </span>
                            )}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>

        <div className="shrink-0 border-t border-white/10 px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-xs font-medium text-gray-300">
                {user?.business_name || 'Vendor account'}
              </p>
              <p className="mt-0.5 text-[10px] uppercase tracking-wide text-gray-500">
                {user?.status || 'active'}
              </p>
            </div>
            <span className="text-[10px] text-gray-600">VendorIQ</span>
          </div>
        </div>
      </aside>
    </>
  );
}
